import {Injectable, signal} from '@angular/core';
import {environment} from '../../../environments/environment';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Chat, ChatMessage, ChatMessageCreate, GetParams, GetResponse, PostResponse} from '@app/shared';
import {Observable, Subject, switchMap, takeUntil, tap, timer} from 'rxjs';
import {convertGetParamsToHttpParams} from '../utils/api-utils';

@Injectable({
  providedIn: 'root'
})
export class CommunicationService {

  refreshNeeded = signal(0);

  /** URLs principales relacionadas con Chat **/
  chatUrl = environment.communicationApiBaseUrl + '/api/chat';
  chatMessageUrl = environment.communicationApiBaseUrl + '/api/chatMessage';
  chatMessageReadersUrl = environment.communicationApiBaseUrl + '/api/chatMessageReaders';
  notificationUrl = environment.communicationApiBaseUrl + '/api/notification';

  constructor(private http: HttpClient) {
  }

  /** Metodos de obtener/crear/actualizar/eliminar Chat **/
  getChat(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.chatUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createChat(newChat: string): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.chatUrl, newChat);
  }

  updateChat(Chat: Chat): Observable<any> {
    return this.http.put(this.chatUrl, Chat);
  }

  deleteChat(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatUrl, {body: {ids: desconsolidationsTypeIds}});
  }

  softDeleteChat(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatUrl + '/soft', {body: {ids: desconsolidationsTypeIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar ChatMessage **/
  getChatMessage(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.chatMessageUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createChatMessage(newChatMessage: ChatMessageCreate): Observable<PostResponse> {
    const formData = new FormData();

    // Campos obligatorios
    formData.append('message', newChatMessage.message);
    formData.append('type', newChatMessage.type);
    formData.append('chatId', newChatMessage.chatId.toString());
    formData.append('userId', newChatMessage.userId.toString());

    // Archivos opcionales
    if (newChatMessage.files) {
      newChatMessage.files.forEach((file) => {
        formData.append(`files`, file);
      });
    }

    // Headers necesarios
    const headers = new HttpHeaders()
      .set('Accept', 'application/json')

    return this.http.post<PostResponse>(this.chatMessageUrl, formData, { headers });
  }

  updateChatMessage(ChatMessage: ChatMessage): Observable<any> {
    return this.http.put(this.chatMessageUrl, ChatMessage);
  }

  deleteChatMessage(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatMessageUrl, {body: {ids: desconsolidationsTypeIds}});
  }

  softDeleteChatMessage(desconsolidationsTypeIds: number[]): Observable<any> {
    return this.http.delete(this.chatMessageUrl + '/soft', {body: {ids: desconsolidationsTypeIds}});
  }

  /** Metodos de obtener/crear/actualizar/eliminar Notification **/
  getNotification(getParams: GetParams): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.notificationUrl, {params: convertGetParamsToHttpParams(getParams)});
  }

  createNotification(newNotification: string): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.notificationUrl, newNotification);
  }

  /**
   * @param refreshBadge en lotes se pasa false y se refresca la campana una sola vez al final:
   * cada refresco vuelve a pedir /pending, que en el servidor enriquece todas las filas.
   */
  updateNotification(id:number, isRead:boolean, refreshBadge = true): Observable<PostResponse> {
    return this.http.put<PostResponse>(this.notificationUrl, {id, isRead}).pipe(
      tap(()=>refreshBadge && this.updateBadge())
    );
  }
  updateBadge(){
    this.refreshNeeded.update(count => count+1)
  }

  deleteNotification(notificationsIds: number[]): Observable<any> {
    return this.http.delete(this.notificationUrl, {body: {ids: notificationsIds}});
  }

  softDeleteNotification(notificationsIds: number[]): Observable<any> {
    return this.http.delete(this.notificationUrl + '/soft', {body: {ids: notificationsIds}});
  }

  getPendingNotification(userId: number): Observable<GetResponse> {
    return this.http.get<GetResponse>(this.notificationUrl+'/pending', {params:{userId:userId}});
  }

  createMessageReader(chatMessageId: number, userId: number): Observable<PostResponse> {
    return this.http.post<PostResponse>(this.chatMessageReadersUrl, {chatMessageId, userId})
  }
}
